import {Feed, Feeds} from '@/api/models/Feeds.ts';
import ConfirmDeleteDialog, {ConfirmFn} from '@/components/ConfirmDeleteDialog.tsx';
import ControlsMenu from '@/components/ControlsMenu.tsx';
import NoSearchResults from '@/components/NoSearchResults.tsx';
import Pending from '@/components/Pending.tsx';
import Search from '@/components/Search.tsx';
import TitleWithDropdown from '@/components/TitleWithDropdown.tsx';
import CloneDialog from '@/pages/feeds/CloneDialog.tsx';
import PreviewDialog from '@/pages/feeds/PreviewDialog.tsx';
import {Toaster, ToastErrors} from '@/toaster.ts';
import {Button, HTMLTable, Icon, MenuDivider, MenuItem, NonIdealState, Tooltip} from '@blueprintjs/core';
import {ReactElement, useCallback, useMemo, useState} from 'react';
import {Link} from 'react-router';

export default function FeedsPage(): ReactElement {
	const [sequence, setSequence] = useState(0);
	const feeds = useMemo(Feeds.list, [sequence]);

	const onRefreshClick = useCallback(() => {
		setSequence(seq => seq + 1);
	}, []);

	const [filter, setFilter] = useState('');

	return (
		<>
			<TitleWithDropdown title="Feeds" rightElement={<Search onSearch={setFilter} />}>
				<Link to="/feeds/new" className="plain-link">
					<MenuItem icon="add" text="New Feed" tagName="span" />
				</Link>

				<MenuItem icon="refresh" text="Refresh" onClick={onRefreshClick} />
			</TitleWithDropdown>

			<Pending resource={feeds}>
				{feeds => <List filter={filter} refresh={onRefreshClick} feeds={feeds} />}
			</Pending>
		</>
	);
}

interface Props {
	feeds: Feed[],
	filter: string,
	refresh: () => void,
}

function List({feeds, filter, refresh}: Props): ReactElement {
	const filteredFeeds = useMemo(() => {
		if (filter.length === 0)
			return feeds;

		return feeds.filter(v => {
			return v.name.toLocaleLowerCase().includes(filter) ||
				v.file.path.toLocaleLowerCase().includes(filter) ||
				v.file.name?.toLocaleLowerCase().includes(filter);
		});
	}, [feeds, filter]);

	const [deleteTarget, setDeleteTarget] = useState<Feed | null>(null);

	const onDeleteClose = useCallback(() => {
		setDeleteTarget(null);
	}, []);

	const onDeleteConfirmed: ConfirmFn = useCallback(async () => {
		if (!deleteTarget)
			throw new Error('onDeleteConfirmed() called without deleteTarget set');

		try {
			await Feeds.remove(deleteTarget.id);
		} catch (error) {
			ToastErrors.fromError(error);
			return;
		}

		setDeleteTarget(null);
		refresh();
	}, [deleteTarget, refresh]);

	const [previewTarget, setPreviewTarget] = useState<Feed | null>(null);

	const onPreviewClose = useCallback(() => {
		setPreviewTarget(null);
	}, []);

	const onIngest: IngestFn = useCallback(async feed => {
		try {
			await Feeds.ingest(feed.id);
		} catch (error) {
			ToastErrors.fromError(error);
			return;
		}

		Toaster.info('Feed queued for import. Please check back in a few minutes.');
	}, []);

	const [cloneTarget, setCloneTarget] = useState<Feed | null>(null);

	const onCloneClose = useCallback(() => setCloneTarget(null), []);

	if (feeds.length === 0) {
		return (
			<NonIdealState
				icon="wind"
				title="Feed me, Seymore..."
				description={(
					<>
						You currently have no feeds.<br />
						Use the button below to create one!
					</>
				)}
				action={(
					<Link to="/feeds/new">
						<Button icon="add" text="Create Feed" variant="outlined" intent="primary" />
					</Link>
				)}
			/>
		);
	} else if (filteredFeeds.length === 0)
		return <NoSearchResults />;

	return (
		<>
			<HTMLTable striped={true} interactive={true} style={{width: '100%'}}>
				<thead>
					<tr>
						<th>ID.</th>
						<th>Name</th>
						<th>Source File</th>
						<th>&nbsp;</th>
					</tr>
				</thead>

				<tbody>
					{filteredFeeds.map(f => (
						<Row
							key={f.id}
							feed={f}
							onDelete={setDeleteTarget}
							onPreview={setPreviewTarget}
							onIngest={onIngest}
							onClone={setCloneTarget}
						/>
					))}
				</tbody>
			</HTMLTable>

			{deleteTarget && (
				<ConfirmDeleteDialog
					confirmText={deleteTarget.name}
					onConfirm={onDeleteConfirmed}
					onClose={onDeleteClose}
					isOpen={true}
				/>
			)}

			{previewTarget && (
				<PreviewDialog feed={previewTarget} onClose={onPreviewClose} />
			)}

			{cloneTarget && (
				<CloneDialog feed={cloneTarget} onClose={onCloneClose} />
			)}
		</>
	);
}

type DeleteFn = (feed: Feed) => void;
type PreviewFn = (feed: Feed) => void;
type IngestFn = (feed: Feed) => void;
type CloneFn = (feed: Feed) => void;

interface RowProps {
	feed: Feed,
	onDelete: DeleteFn,
	onPreview: PreviewFn,
	onIngest: IngestFn,
	onClone: CloneFn,
}

function Row({feed, onDelete, onPreview, onIngest, onClone}: RowProps): ReactElement {
	const onDeleteClick = useCallback(() => {
		onDelete(feed);
	}, [feed, onDelete]);

	const onPreviewClick = useCallback(() => {
		onPreview(feed);
	}, [feed, onPreview]);

	const onIngestClick = useCallback(() => {
		onIngest(feed);
	}, [feed, onIngest]);

	const onCloneClick = useCallback(() => {
		onClone(feed);
	}, [feed, onClone]);

	return (
		<tr>
			<td>{feed.id}</td>
			<td>{feed.name}</td>

			<td>
				<Link to={`/files/${feed.file.id}`}>
					{feed.file.name ?? `Feed #${feed.file.id}`} <Icon
					icon="share"
					style={{marginLeft: 5, paddingBottom: 2}}
				/>
				</Link>
			</td>

			<td style={{display: 'flex', justifyContent: 'end', gap: 10}}>
				<Tooltip content="Edit" placement="bottom">
					<Link to={`/feeds/${feed.id}`}>
						<Button icon="edit" variant="minimal" size="small" />
					</Link>
				</Tooltip>

				<ControlsMenu>
					<MenuItem icon="import" text="Import Feed Now" onClick={onIngestClick} />
					<MenuItem icon="eye-open" text="Preview" onClick={onPreviewClick} />
					<MenuItem icon="duplicate" text="Duplicate Feed" onClick={onCloneClick} />

					<MenuDivider />

					<MenuItem intent="danger" icon="delete" text="Delete Feed" onClick={onDeleteClick} />
				</ControlsMenu>
			</td>
		</tr>
	);
}
